import React, { useEffect, useState } from "react";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import {
	Alert,
	Button,
	Divider,
	Form,
	InputNumber,
	Select,
	Skeleton,
	Space,
	Typography,
	Upload,
	message,
} from "antd";
import { YouTubeLiveStream } from "./types";
import { authAxios } from "../utils/session_utils";
import { useOutletContext } from "react-router-dom";

const { Text } = Typography;

const frequencyOptions = [
	{ label: "Every 15 Minutes", value: 15, unitType: "minute" },
	{ label: "Every 30 Minutes", value: 30, unitType: "minute" },
	{ label: "Every 45 Minutes", value: 45, unitType: "minute" },
	{ label: "Every 1 Hour", value: 1, unitType: "hour" },
	{ label: "Every 2 Hours", value: 2, unitType: "hour" },
	{ label: "Every 4 Hours", value: 4, unitType: "hour" },
];

const durationOptions = [
	{ label: "15 Seconds", value: 15, unitType: "second" },
	{ label: "30 Seconds", value: 30, unitType: "second" },
	{ label: "1 Minute", value: 1, unitType: "minute" },
	{ label: "2 Minutes", value: 2, unitType: "minute" },
];

interface OverlayEntry {
	id?: string;
	frequency: number | null;
	duration: number | null;
	offset: number;
	overlayImage: any[];
}

interface StreamBuilderProps {
	stream?: YouTubeLiveStream | null;
	onClose: () => void;
	onSaved: (streamId: string) => void;
}

const toSeconds = (value: number, unit: string): number => {
	if (unit === "minute") return value * 60;
	if (unit === "hour") return value * 3600;
	return value;
};

const fromSeconds = (
	totalSeconds: number,
	options: { value: number; unitType: string }[]
): number | null =>
	options.find(o => toSeconds(o.value, o.unitType) === totalSeconds)?.value ?? null;

const emptyOverlay = (): OverlayEntry => ({
	frequency: null,
	duration: null,
	offset: 0,
	overlayImage: [],
});

const OverlayBuilder: React.FC<StreamBuilderProps> = ({ stream, onClose, onSaved }) => {
	const [overlays, setOverlays] = useState<OverlayEntry[]>([emptyOverlay()]);
	const [deletedOverlayIds, setDeletedOverlayIds] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);
	const [fetchingOverlays, setFetchingOverlays] = useState(false);
	const [error, setError] = useState("");
	const { user } = useOutletContext<{ user: any }>();

	useEffect(() => {
		if (!stream?.id) return;

		const fetchOverlays = async () => {
			setFetchingOverlays(true);
			try {
				const res = await authAxios({
					method: "GET",
					url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/overlay/${stream.id}`,
				});

				const data = res.data?.data;

				if (data?.length) {
					setOverlays(
						data.map((o: any) => ({
							id: o.id,
							frequency:
								o.overlay_frequency != null
									? fromSeconds(o.overlay_frequency, frequencyOptions)
									: null,
							duration:
								o.overlay_duration != null
									? fromSeconds(o.overlay_duration, durationOptions)
									: null,
							offset: o.overlay_offset ?? 0,
							overlayImage: o.overlay_image_url
								? [{ uid: o.id, name: "overlay_image", status: "done", url: o.overlay_image_url }]
								: [],
						}))
					);
				} else {
					setOverlays([emptyOverlay()]);
				}
			} catch (err) {
				console.error("Failed to fetch overlays:", err);
				setOverlays([emptyOverlay()]);
			} finally {
				setFetchingOverlays(false);
			}
		};

		fetchOverlays();
	}, [stream?.id]);

	const updateOverlay = <K extends keyof OverlayEntry>(
		index: number,
		key: K,
		value: OverlayEntry[K]
	) => {
		setOverlays(prev => prev.map((o, i) => (i === index ? { ...o, [key]: value } : o)));
	};

	const addOverlay = () => setOverlays(prev => [...prev, emptyOverlay()]);

	const removeOverlay = (index: number) => {
		const overlay = overlays[index];
		const { id } = overlay;
		if (id) {
			setDeletedOverlayIds(prev => [...prev, id]);
		}
		setOverlays(prev => prev.filter((_, i) => i !== index));
	};

	const validate = (): string | null => {
		for (let i = 0; i < overlays.length; i++) {
			const o = overlays[i];
			const label = `Overlay ${i + 1}`;
			if (!o.overlayImage.length) return `${label}: please upload an image.`;
			if (o.frequency === null) return `${label}: please select a frequency.`;
			if (o.duration === null) return `${label}: please select a duration.`;
			if (o.offset < 0) return `${label}: offset cannot be negative.`;
		}
		return null;
	};

	const handleSave = async () => {
		const validationError = validate();
		if (validationError) {
			setError(validationError);
			return;
		}
		if (!stream?.id) {
			setError("Stream ID is missing.");
			return;
		}

		setLoading(true);
		setError("");

		try {
			// Save all overlays (POST for new, PATCH for existing)
			await Promise.all(
				overlays.map(async (overlay, index) => {
					const selectedDuration = durationOptions.find(o => o.value === overlay.duration);
					const selectedFrequency = frequencyOptions.find(o => o.value === overlay.frequency);

					if (!selectedDuration || !selectedFrequency) {
						throw new Error(`Overlay ${index + 1}: invalid duration or frequency selected.`);
					}

					const formData = new FormData();
					formData.append("user_id", user.id);
					formData.append("duration", String(overlay.duration));
					formData.append("duration_unit", selectedDuration.unitType);
					formData.append("frequency", String(overlay.frequency));
					formData.append("frequency_unit", selectedFrequency.unitType);
					formData.append("offset", String(overlay.offset));

					const file = overlay.overlayImage[0];
					if (file?.originFileObj) {
						formData.append("overlay_image", file.originFileObj);
					}

					const url = overlay.id
						? `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/overlay/${stream.id}/${overlay.id}`
						: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/overlay/${stream.id}`;

					await authAxios({
						method: overlay.id ? "PATCH" : "POST",
						url,
						data: formData,
						headers: { "Content-Type": "multipart/form-data" },
					});
				})
			);

			// Delete overlays that were removed from the UI
			await Promise.all(
				deletedOverlayIds.map(overlayId =>
					authAxios({
						method: "DELETE",
						url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/overlay/${stream.id}/${overlayId}`,
					})
				)
			);

			message.success("Overlays saved successfully");
			onSaved(stream.id);
		} catch (err) {
			console.error("Error saving overlays:", err);
			setError("Failed to save overlays: " + (err instanceof Error ? err.message : String(err)));
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		setOverlays([emptyOverlay()]);
		setDeletedOverlayIds([]);
		setError("");
		onClose();
	};

	const beforeUpload = (file: File) => {
		const isImage = file.type.startsWith("image/");
		if (!isImage) message.error("You can only upload image files!");
		return isImage || Upload.LIST_IGNORE;
	};

	if (fetchingOverlays) {
		return <Skeleton active paragraph={{ rows: 4 }} />;
	}

	return (
		<div>
			{error && (
				<Alert
					message="Error"
					description={error}
					type="error"
					closable
					onClose={() => setError("")}
					style={{ marginBottom: 16 }}
				/>
			)}

			{overlays.map((overlay, index) => (
				<div
					key={overlay.id ?? index}
					style={{
						border: "1px solid #d9d9d9",
						borderRadius: 8,
						padding: "16px 16px 8px",
						marginBottom: 16,
						background: "#fafafa",
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							marginBottom: 12,
						}}
					>
						<Text strong style={{ fontSize: 13 }}>
							Overlay {index + 1}
						</Text>
						{overlays.length > 1 && (
							<Button
								type="text"
								danger
								icon={<DeleteOutlined />}
								size="small"
								onClick={() => removeOverlay(index)}
							/>
						)}
					</div>

					<Form layout="horizontal" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
						<Form.Item label="Upload" required style={{ marginBottom: 12 }}>
							<Upload
								listType="picture-card"
								fileList={overlay.overlayImage}
								beforeUpload={beforeUpload}
								maxCount={1}
								onChange={({ fileList }) => updateOverlay(index, "overlayImage", fileList)}
							>
								{overlay.overlayImage.length < 1 && (
									<button
										style={{ color: "inherit", cursor: "pointer", border: 0, background: "none" }}
										type="button"
									>
										<PlusOutlined />
										<div style={{ marginTop: 6 }}>Upload</div>
									</button>
								)}
							</Upload>
						</Form.Item>

						<Form.Item label="Frequency" required style={{ marginBottom: 12 }}>
							<Select
								options={frequencyOptions}
								value={overlay.frequency}
								onChange={val => updateOverlay(index, "frequency", val)}
								placeholder="Select frequency"
							/>
						</Form.Item>

						<Form.Item label="Duration" required style={{ marginBottom: 12 }}>
							<Select
								options={durationOptions}
								value={overlay.duration}
								onChange={val => updateOverlay(index, "duration", val)}
								placeholder="Select duration"
							/>
						</Form.Item>

						<Form.Item
							label={
								<span>
									Offset{" "}
									<Text type="secondary" style={{ fontSize: 12, fontWeight: "normal" }}>
										(in seconds)
									</Text>
								</span>
							}
							style={{ marginBottom: 4 }}
						>
							<InputNumber
								min={0}
								value={overlay.offset}
								onChange={val => updateOverlay(index, "offset", val ?? 0)}
								style={{ width: "100%" }}
								placeholder="0"
							/>
						</Form.Item>
					</Form>
				</div>
			))}

			<Button
				type="dashed"
				icon={<PlusOutlined />}
				onClick={addOverlay}
				style={{ width: "100%", marginBottom: 16 }}
			>
				Add an Overlay
			</Button>

			<Divider style={{ margin: "8px 0 16px" }} />

			<Space style={{ display: "flex", justifyContent: "flex-end" }}>
				<Button onClick={handleCancel}>Close</Button>
				<Button type="primary" loading={loading} onClick={handleSave}>
					Save Details
				</Button>
			</Space>
		</div>
	);
};

export default OverlayBuilder;
